import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { login } from '../services/api';

const Login: React.FC = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const data = await login({ username, password });
            // Store token and role
            localStorage.setItem('access_token', data.accessToken);
            if (data.role) {
                localStorage.setItem('user_role', data.role);
            }
            navigate('/');
        } catch (err: any) {
            console.error('Login failed:', err);
            
            // Parse error response for user-friendly message
            let errorMessage = 'Login failed. Please check your credentials.';
            
            if (err.response?.data) {
                const errorData = err.response.data;
                
                // Check if detail contains JSON-like error object
                if (typeof errorData.detail === 'string') {
                    try {
                        const parsedDetail = JSON.parse(errorData.detail);
                        
                        // Handle specific error types
                        if (parsedDetail.error === 'too_many_attempts') {
                            errorMessage = 'Too many failed login attempts. Your account has been temporarily locked for security. Please contact the administrator.';
                        } else if (parsedDetail.error_description) {
                            errorMessage = parsedDetail.error_description;
                        } else {
                            errorMessage = parsedDetail.error || errorMessage;
                        }
                    } catch {
                        // If not JSON, use the detail as is
                        errorMessage = errorData.detail;
                    }
                } else if (errorData.detail) {
                    errorMessage = errorData.detail;
                } else if (errorData.message) {
                    errorMessage = errorData.message;
                }
            }
            
            // Handle specific HTTP status codes
            if (err.response?.status === 401) {
                if (!errorMessage.includes('Too many')) {
                    errorMessage = 'Invalid username or password. Please try again.';
                }
            } else if (err.response?.status === 403) {
                errorMessage = 'Access forbidden. Your account may be disabled.';
            } else if (err.response?.status === 500) {
                errorMessage = 'Server error. Please try again later or contact support.';
            } else if (!err.response) {
                errorMessage = 'Unable to connect to the server. Please check your internet connection.';
            }
            
            setError(errorMessage);
        }
    };

    return (
        <div className="auth-container">
            <div className="auth-left">
                <div className="auth-form-container">
                    <h1>Log In</h1>
                    <p className="auth-subtitle">Track your products on social media activity effortlessly.</p>

                    {error && (
                        <div className="error-message" style={{ 
                            color: '#991b1b', 
                            backgroundColor: '#fef2f2',
                            border: '1.5px solid #f87171',
                            borderRadius: '10px',
                            padding: '1rem 1rem',
                            marginBottom: '1.5rem',
                            fontSize: '0.9rem',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '0.75rem',
                            justifyContent: 'space-between',
                            position: 'relative',
                            lineHeight: '1.6',
                            boxShadow: '0 1px 3px rgba(220, 38, 38, 0.1)'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', flex: 1 }}>
                                <svg width="22" height="22" viewBox="0 0 20 20" fill="currentColor" style={{ flexShrink: 0, marginTop: '2px' }}>
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: '600', marginBottom: '0.25rem', fontSize: '0.95rem' }}>
                                        Login Failed
                                    </div>
                                    <div style={{ color: '#7f1d1d', fontWeight: '400', fontSize: '0.875rem' }}>
                                        {error}
                                    </div>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => setError('')}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#dc2626',
                                    cursor: 'pointer',
                                    padding: '0.25rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '6px',
                                    transition: 'all 0.2s',
                                    flexShrink: 0,
                                    marginTop: '2px'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = 'rgba(220, 38, 38, 0.15)';
                                    e.currentTarget.style.transform = 'scale(1.1)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.style.transform = 'scale(1)';
                                }}
                                aria-label="Close error message"
                            >
                                <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </button>
                        </div>
                    )}

                    <form onSubmit={handleLogin}>
                        <div className="form-group">
                            <label htmlFor="username">Username</label>
                            <input
                                type="text"
                                id="username"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                placeholder="Enter your username"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="password">Password</label>
                            <div className="password-input-wrapper">
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    id="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Enter your password"
                                />
                                <button
                                    type="button"
                                    className="toggle-password"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                </button>
                            </div>
                        </div>

                        <div className="form-options">
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={rememberMe}
                                    onChange={(e) => setRememberMe(e.target.checked)}
                                />
                                Remember me
                            </label>
                            <Link to="/forgot-password">Forgot password?</Link>
                        </div>

                        <button type="submit" className="btn-primary btn-block">
                            Sign In
                        </button>
                    </form>
                </div>
            </div>
            <div className="auth-right">
                <div className="auth-branding">
                    <div className="logo-container white">
                        <img src="/logo.svg" alt="Social Mind" className="logo-icon" />
                        <div className="logo-text">
                            <h2>Social Mind</h2>
                            <span>By Iterate.ai</span>
                        </div>
                    </div>
                    <div className="auth-hero-text">
                        <h2>Welcome to Social Mind where social management meets effortless engagement.</h2>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;
